/**
 * Sheet 10, run history (mission 6 WP09 T050, T051; FR-019, FR-023, NFR-005,
 * C-006, C-009; D.5, D.2, ANA.9, B.3, U.2, GEN.6, GL.1; sheet 10 callouts 1–3,
 * 5–8 and 10; research §1.20; post-tasks P12, E2; T1, T13).
 *
 * In order: the header (the page title, the current rule-set version from
 * `['rules']`, and New run), the stat row (Balance and Reserved, 10/2), the
 * ANA.4 legend (the table's count cells need it on the page, T13), the
 * filters, then the table and its footer.
 *
 * - New run is the primary and is never disabled for credits: validation is free
 *   (U.2) and the credit block is at start (B.3). Mission 9 WP14 T089 (10/1, 51/9;
 *   FR-015; B.1; U11, U30): while `useBillingGate` refuses (read-only, pending) it
 *   is a non-link Button, demoted to secondary and relabeled `gatedReadOnly`, with
 *   the reason in its tooltip; dunning keeps full access.
 * - The `state` and `path` filters live in the URL and go to the API (C-006).
 *   Only the seven contract states are offered; an invalid value is dropped
 *   from the URL with replace. A filter the API refuses clears the filters.
 * - Paging walks `next_cursor` 50 rows at a time: `lib/api/runs` sends
 *   `limit=50`, and the cursor stack is component state, never a key or the
 *   URL. A filter change starts again at the first page in the same render. A
 *   refused cursor offers "Back to the first page" (NFR-005; owner decision 2026-09-26).
 * - The §7.14 empty state replaces the table only when the first page is
 *   empty with no filter; a filter that matches nothing keeps the table.
 * - The lineage view is absent (C-009).
 * - Mission 10 WP18 T104 (FR-019 10/9, FR-002, C-002; C-001 names the marker): a run whose
 *   `class1_deleted_at` is set reads register 37 `findingsExpired`, a §2.6 neutral badge whose
 *   `<time dateTime>` is the marker, with the date in its title. The row stays, with its counts.
 */
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { History } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'

import { EmptyState } from '@/components/trellis/EmptyState'
import { SeverityLegend } from '@/components/trellis/SeverityLegend'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ApiError } from '@/lib/api'
import { RUN_PATHS, type KnownRunState, type RunFilters, type RunPath, type RunSummary } from '@/lib/api/runs'
import { gateButton, useBillingGate } from '@/lib/billing-gate'
import { Mono, date, isoDateTime, time } from '@/lib/format'
import { copyText } from '@/lib/provisional-copy'
import { rulesQuery, runsQuery } from '@/lib/queries'

import { HistoryStats } from './history/HistoryStats'
import { HistoryTable, PATH_WORDS, stateWord } from './history/HistoryTable'

/** The `state` values GET /v1/runs accepts (mission 5 results-api.md); `validating` is not one. */
const FILTER_STATES = [
  'queued',
  'generating',
  'analyzing',
  'ready',
  'rejected',
  'failed',
  'cancelled',
] as const satisfies readonly KnownRunState[]
type FilterState = (typeof FILTER_STATES)[number]

const ALL = 'all'

function oneOf<T extends string>(value: string | null, allowed: readonly T[]): T | undefined {
  return allowed.find((v) => v === value)
}

interface Page {
  cursor: string
  /** Rows before this page, for the footer range. */
  offset: number
}

/** 10/9: "Findings expired" on a run the sweep deleted the Class 1 data of (FR-019; C-001's marker). */
function findingsExpired(run: RunSummary) {
  const at = run.class1_deleted_at
  if (!at) return null
  return (
    <Badge status="neutral" data-findings-expired="">
      <time dateTime={isoDateTime(at)} title={`${date(at)} ${time(at)}`}>
        {copyText('findingsExpired')}
      </time>
    </Badge>
  )
}

function NewRun() {
  const gate = useBillingGate()
  if (gate.refused) {
    return (
      <Button variant="secondary" {...gateButton(gate)}>
        {copyText('gatedReadOnly')}
      </Button>
    )
  }
  return (
    <Button asChild>
      <Link to="/app/runs/new">New run</Link>
    </Button>
  )
}

function Header() {
  const rules = useQuery(rulesQuery())
  return (
    <div className="flex flex-wrap items-center gap-4">
      <h1 className="text-h1 text-navy-950">{copyText('crumb.runs')}</h1>
      {rules.data ? (
        <p className="text-body-sm text-navy-600">
          {copyText('ruleSetVersionLabel')} <Mono className="text-navy-950">{rules.data.rule_set_version}</Mono>
        </p>
      ) : null}
      <div className="ml-auto">
        <NewRun />
      </div>
    </div>
  )
}

interface FilterProps {
  id: string
  label: string
  value: string | undefined
  options: ReadonlyArray<[value: string, label: string]>
  onChange(value: string | undefined): void
}

function Filter({ id, label, value, options, onChange }: FilterProps) {
  return (
    <div className="flex w-56 flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value ?? ALL} onValueChange={(v) => onChange(v === ALL ? undefined : v)}>
        <SelectTrigger id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>All</SelectItem>
          {options.map(([v, text]) => (
            <SelectItem key={v} value={v}>
              {text}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function isFilterRefusal(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.problem.status === 400 &&
    (error.problem.errors ?? []).some((e) => e.field === 'state' || e.field === 'path')
  )
}

export default function RunHistory() {
  const [params, setParams] = useSearchParams()
  const rawState = params.get('state')
  const rawPath = params.get('path')
  const state = oneOf<FilterState>(rawState, FILTER_STATES)
  const path = oneOf<RunPath>(rawPath, RUN_PATHS)
  const badState = rawState !== null && state === undefined
  const badPath = rawPath !== null && path === undefined

  useEffect(() => {
    if (!badState && !badPath) return
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (badState) next.delete('state')
        if (badPath) next.delete('path')
        return next
      },
      { replace: true },
    )
  }, [badState, badPath, setParams])

  const scope = `${state ?? ''}|${path ?? ''}`
  const [paging, setPaging] = useState<{ scope: string; stack: Page[] }>({ scope, stack: [] })
  const stack = paging.scope === scope ? paging.stack : []
  const page = stack.at(-1)

  const filters: RunFilters = {}
  if (state !== undefined) filters.state = state
  if (path !== undefined) filters.path = path
  if (page !== undefined) filters.cursor = page.cursor
  const runs = useQuery(runsQuery(filters))

  useEffect(() => {
    if (isFilterRefusal(runs.error)) setParams(new URLSearchParams(), { replace: true })
  }, [runs.error, setParams])

  function setFilter(key: 'state' | 'path', value: string | undefined) {
    setParams((prev) => {
      const next = new URLSearchParams(prev)
      if (value === undefined) next.delete(key)
      else next.set(key, value)
      return next
    })
  }

  const settled = runs.data !== undefined && !runs.isPlaceholderData
  const rows = settled ? runs.data.entries : []
  const offset = page?.offset ?? 0
  const firstRun = settled && rows.length === 0 && state === undefined && path === undefined && page === undefined
  const nextCursor = settled ? runs.data.next_cursor : undefined

  return (
    <div className="flex flex-col gap-6">
      <Header />
      <HistoryStats />
      <SeverityLegend />
      {firstRun ? (
        <EmptyState
          icon={History}
          title={copyText('historyEmptyTitle')}
          description={copyText('historyEmptyBody')}
          action={<NewRun />}
          links={[
            { to: '/docs/layout/v1', label: copyText('historyEmptyLinks.layoutSpec') },
            { to: '/app/settings/header', label: copyText('historyEmptyLinks.headerParams') },
          ]}
        />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-4">
            <Filter
              id="history-state"
              label="State"
              value={state}
              options={FILTER_STATES.map((s) => [s, stateWord(s)])}
              onChange={(v) => setFilter('state', v)}
            />
            <Filter
              id="history-path"
              label="Type"
              value={path}
              options={RUN_PATHS.map((p) => [p, PATH_WORDS[p]])}
              onChange={(v) => setFilter('path', v)}
            />
          </div>
          <HistoryTable
            rows={rows}
            loading={!settled && !runs.isError}
            error={runs.isError ? runs.error : null}
            paging={{
              offset,
              hasPrevious: stack.length > 0,
              hasNext: nextCursor !== undefined,
              onPrevious: () => setPaging({ scope, stack: stack.slice(0, -1) }),
              onNext: () => {
                if (nextCursor !== undefined) {
                  setPaging({ scope, stack: [...stack, { cursor: nextCursor, offset: offset + rows.length }] })
                }
              },
            }}
            onStartAgain={() => setPaging({ scope, stack: [] })}
            note={findingsExpired}
          />
        </div>
      )}
    </div>
  )
}

